class MedievalStockAlternatives extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.owner = this.closest('product-info');
    this.toggle = this.querySelector('[data-stock-alternatives-toggle]');
    this.toggleLabel = this.querySelector('[data-stock-alternatives-label]');
    this.panel = this.querySelector('[data-stock-alternatives-panel]');
    this.results = this.querySelector('[data-stock-alternatives-results]');
    this.error = this.querySelector('[data-stock-alternatives-error]');
    this.context = JSON.parse(this.querySelector('[data-stock-context]')?.textContent || '{}');
    this.cards = [];
    this.loaded = false;
    this.owner?.addEventListener('tm:variant-change', (event) => this.update(event.detail.variant), { signal });
    this.toggle?.addEventListener('click', () => this.setOpen(this.panel.hidden), { signal });
    this.querySelector('[data-stock-alternatives-close]')?.addEventListener('click', () => this.setOpen(false), { signal });
    this.panel.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.setOpen(false);
    }, { signal });
    this.panel.addEventListener('close', () => {
      if (!this.panel.open) {
        this.panel.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'false');
      }
    }, { signal });
    this.panel.addEventListener('click', (event) => {
      if (event.target !== this.panel) return;
      const bounds = this.panel.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.setOpen(false);
    }, { signal });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-stock-alternative-variant]')) this.updateCard(event.target.closest('[data-stock-alternative-card]'));
    }, { signal });
    this.addEventListener('submit', (event) => this.checkBeforeAdd(event), { signal });
    document.addEventListener('cart:added', () => this.refresh(), { signal });
    window.addEventListener('resize', () => this.fitToggle(), { signal });
    document.fonts?.ready.then(() => {
      if (!signal.aborted) this.fitToggle();
    });
    this.update(this.owner?.variants?.find((variant) => String(variant.id) === this.dataset.selectedVariantId));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.loading = false;
  }

  normalize(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  }

  word(value) {
    const word = this.normalize(value);
    const aliases = {
      zapatos: 'zapato', shoes: 'zapato', shoe: 'zapato',
      botas: 'bota', boots: 'bota', boot: 'bota',
      botines: 'botin', mocasines: 'mocasin', escarpines: 'escarpin',
      sandals: 'sandalia', sandalias: 'sandalia', chanclas: 'chancla',
      marrones: 'marron', brown: 'marron', leather: 'cuero', piel: 'cuero',
      negra: 'negro', negras: 'negro', negros: 'negro', black: 'negro',
      blanca: 'blanco', blancas: 'blanco', blancos: 'blanco',
      roja: 'rojo', rojas: 'rojo', rojos: 'rojo',
      medievales: 'medieval', vikingas: 'vikingo', vikingos: 'vikingo',
      vikinga: 'vikingo', celtas: 'celta', romanas: 'romano', romanos: 'romano',
      romana: 'romano', japones: 'japones', japonesa: 'japones',
      japoneses: 'japones', japonesas: 'japones', swords: 'espada', sword: 'espada',
      daggers: 'daga', dagger: 'daga', helmets: 'casco', helmet: 'casco',
    };
    if (aliases[word]) return aliases[word];
    if (/[aeiou]s$/.test(word)) return word.slice(0, -1);
    if (/[lnrd]es$/.test(word)) return word.slice(0, -2);
    return word;
  }

  words(value) {
    return this.normalize(value).replace(/[^a-z0-9]+/g, ' ').split(' ')
      .filter((word) => word.length > 2).map((word) => this.word(word));
  }

  identity(title, type = '') {
    const ignored = new Set(['para', 'con', 'por', 'del', 'los', 'las', 'lo', 'la', 'una', 'uno',
      'replica', 'reproduccion', 'pack', 'set', 'juego', 'par', 'nuevo', 'nueva']);
    const titleWords = this.words(title).filter((word) => !ignored.has(word));
    const typeWords = this.words(type).filter((word) => !ignored.has(word)
      && !['general', 'default', 'producto', 'product'].includes(word));
    const head = titleWords[0] || typeWords[0] || '';
    const footwear = {
      zapato: 'shoe', mocasin: 'shoe', escarpin: 'shoe', calzado: 'shoe',
      bota: 'boot', botin: 'boot', sandalia: 'sandal', chancla: 'sandal',
    };
    const kind = footwear[head] || head;
    const family = ['shoe', 'boot'].includes(kind) ? 'closed-footwear'
      : kind === 'sandal' ? 'open-footwear' : head;
    return { head, kind, family, words: new Set(this.words(title)) };
  }

  collections() {
    const broad = new Set(['all', 'frontpage', 'prestashop', 'novedades', 'ofertas',
      'rebajas', 'promociones', 'todos', 'todos-los-productos', 'productos', 'marcas',
      'ropa', 'complementos', 'complementos-ropa', 'larp-y-cosplay', this.context.vendor]);
    const source = this.identity(this.context.title, this.context.type);
    const candidates = (this.context.collections || []).filter((item) => !broad.has(item.handle)
      && this.normalize(item.title) !== this.normalize(this.context.vendorName)
      && Number(item.count) > 0 && Number(item.count) <= 3000 && item.url)
      .sort((a, b) => a.count - b.count);
    const specific = candidates.filter((item) => {
      const words = this.words([item.handle, item.title].join(' '));
      if (source.family.endsWith('footwear')) {
        return words.some((word) => ['calzado', 'zapato', 'bota', 'botin', 'mocasin', 'sandalia'].includes(word));
      }
      return source.head && words.includes(source.head);
    });
    return (specific.length ? specific : candidates.filter((item) => item.count <= 500)).slice(0, 2);
  }

  purpose(title, tags) {
    const text = this.normalize([title, ...tags].join(' '));
    if (/\blarp\b/.test(text)) return 'larp';
    if (/\b(decorativ[ao]s?|ornamental(es)?)\b/.test(text)) return 'decorative';
    if (/\b(entrenamiento|practica|training)\b/.test(text)) return 'training';
    if (/\b(funcional(es)?|battle ready|battle-ready)\b/.test(text)) return 'functional';
    return '';
  }

  similar(card) {
    if (card.dataset.productId === this.dataset.productId) return false;
    const source = this.identity(this.context.title, this.context.type);
    const candidate = this.identity(card.dataset.title, card.dataset.type);
    if (!source.family || source.family !== candidate.family) return false;
    const weapons = new Set(['espada', 'espadon', 'sable', 'katana', 'daga', 'cuchillo',
      'hacha', 'lanza', 'alabarda', 'maza', 'martillo', 'arco', 'ballesta']);
    if (weapons.has(source.head)) {
      const sourcePurpose = this.purpose(this.context.title, this.context.tags || []);
      const candidatePurpose = this.purpose(card.dataset.title, JSON.parse(card.dataset.tags || '[]'));
      if ((sourcePurpose === 'larp') !== (candidatePurpose === 'larp')) return false;
      if (sourcePurpose && sourcePurpose !== candidatePurpose) return false;
    }
    return Boolean(this.preferredOption(card));
  }

  affinity(title, type = '', tags = []) {
    const family = this.identity(title, type).head;
    const aliases = { templaria: 'templario', gotica: 'gotico', gladiu: 'gladius', renacimiento: 'renacentista' };
    const featureWords = (text) => new Set(this.words(text).map((word) => aliases[word] || word));
    const titleWords = featureWords(title);
    const words = new Set([...titleWords, ...featureWords(tags.join(' '))]);
    const subtypesByFamily = {
      espada: [
        ['rapiera', ['rapiera', 'rapier']],
        ['gladius', ['gladius']],
        ['falcata', ['falcata']],
      ],
      cuchillo: [
        ['seax', ['seax']],
        ['bowie', ['bowie']],
        ['kukri', ['kukri']],
        ['jamonero', ['jamonero']],
      ],
    };
    const subtypeRules = subtypesByFamily[family] || [];
    const findSubtypes = (tokens) => subtypeRules
      .filter(([, terms]) => terms.some((term) => tokens.has(term))).map(([name]) => name);
    const titleSubtypes = findSubtypes(titleWords);
    const subtypes = titleSubtypes.length ? titleSubtypes : findSubtypes(words);
    const details = family === 'espada' && words.has('cazoleta') ? ['cazoleta'] : [];
    const knownStyles = ['pirata', 'vikingo', 'celta', 'romano', 'renacentista',
      'medieval', 'templario', 'samurai', 'japones', 'gotico'];
    const titleStyles = knownStyles.filter((style) => titleWords.has(style));
    let styles = titleStyles.length ? titleStyles : knownStyles.filter((style) => words.has(style));
    if (styles.some((style) => style !== 'medieval')) styles = styles.filter((style) => style !== 'medieval');
    return { subtypes, details, styles };
  }

  affinityRank(card, source) {
    const hasSubtypes = source.subtypes.length > 0;
    const hasDetails = source.details.length > 0;
    if (!hasSubtypes && !hasDetails && !source.styles.length) {
      return { tier: 0, styleMatches: 0, general: false };
    }
    const candidate = this.affinity(card.dataset.title, card.dataset.type, JSON.parse(card.dataset.tags || '[]'));
    const subtypeMatch = hasSubtypes && source.subtypes.some((subtype) => candidate.subtypes.includes(subtype));
    const detailMatch = hasDetails && source.details.every((detail) => candidate.details.includes(detail));
    const styleMatches = source.styles.filter((style) => candidate.styles.includes(style)).length;
    let tier = 4;
    if (hasSubtypes && hasDetails && subtypeMatch && detailMatch) tier = 0;
    else if (subtypeMatch) tier = hasDetails ? 1 : 0;
    else if (detailMatch) tier = hasSubtypes ? 2 : 0;
    else if (!hasSubtypes && !hasDetails && styleMatches) tier = 0;
    else if (source.styles.some((style) => style !== 'medieval' && candidate.styles.includes(style))) tier = 3;
    return { tier, styleMatches, general: tier === 4 };
  }

  alternatives() {
    const features = this.affinity(this.context.title, this.context.type, this.context.tags || []);
    return this.cards.filter((card) => this.similar(card))
      .map((card) => ({ card, ...this.affinityRank(card, features), score: this.score(card) }))
      .sort((a, b) => a.tier - b.tier || b.styleMatches - a.styleMatches || b.score - a.score)
      .slice(0, 12);
  }

  requestedCategory() {
    const source = this.identity(this.context.title, this.context.type);
    const features = this.affinity(this.context.title, this.context.type, this.context.tags || []);
    const descriptors = [...features.subtypes, ...features.details, ...features.styles];
    const categories = [
      ...(this.context.collections || []),
      ...(this.context.tags || []).map((title) => ({ title })),
    ].map((category) => {
      const words = this.words(category.title);
      const affinity = this.affinity(category.title, this.context.type);
      const categoryDescriptors = [...affinity.subtypes, ...affinity.details, ...affinity.styles];
      return { ...category, words,
        matches: descriptors.filter((word) => categoryDescriptors.includes(word)).length,
        shared: words.filter((word) => source.words.has(word)).length };
    }).filter((category) => category.words.includes(source.head)
      && this.normalize(category.title) !== this.normalize(this.context.vendorName)
      && (!descriptors.length || category.matches > 0))
      .sort((a, b) => b.matches - a.matches || b.shared - a.shared
        || b.words.length - a.words.length || Number(a.count || Infinity) - Number(b.count || Infinity));
    return categories[0] || null;
  }

  matchesCategory(card, category) {
    const collections = JSON.parse(card.dataset.collections || '[]');
    if (category.id && collections.some((id) => String(id) === String(category.id))) return true;
    const tags = JSON.parse(card.dataset.tags || '[]');
    return [card.dataset.title, ...tags].some((text) => {
      const words = this.words(text);
      return category.words.every((word) => words.includes(word));
    });
  }

  otherAlternativesLabel() {
    const family = this.identity(this.context.title, this.context.type).head;
    const labels = {
      espada: this.dataset.otherSwordsLabel,
      cuchillo: this.dataset.otherKnivesLabel,
      chaleco: this.dataset.otherVestsLabel,
    };
    return labels[family] || this.dataset.otherProductsLabel;
  }

  sizeIndex(names) {
    return names.findIndex((name) => /^(tallas?|sizes?|tamano|medida|numero)$/.test(this.normalize(name)));
  }

  sameSize(card, option = this.preferredOption(card)) {
    const sourceIndex = this.sizeIndex(this.context.optionNames || []);
    const candidateIndex = this.sizeIndex(JSON.parse(card.dataset.optionNames || '[]'));
    const sourceValue = this.currentVariant?.options?.[sourceIndex];
    if (!option || sourceIndex < 0 || candidateIndex < 0 || !sourceValue) return false;
    const values = JSON.parse(option.dataset.options || '[]');
    return this.normalize(values[candidateIndex]) === this.normalize(sourceValue);
  }

  preferredOption(card) {
    const options = [...card.content.querySelectorAll('option')];
    const sourceNames = (this.context.optionNames || []).map((name) => this.normalize(name));
    const candidateNames = JSON.parse(card.dataset.optionNames || '[]').map((name) => this.normalize(name));
    const ranked = options.map((option, index) => {
      const values = JSON.parse(option.dataset.options || '[]');
      const matches = values.filter((value, position) => {
        const sourceIndex = sourceNames.indexOf(candidateNames[position]);
        return sourceIndex >= 0 && this.normalize(value) === this.normalize(this.currentVariant?.options?.[sourceIndex]);
      }).length;
      return { option, rank: (this.sameSize(card, option) ? 100 : 0) + matches, index };
    }).sort((a, b) => b.rank - a.rank || a.index - b.index);
    return ranked[0]?.option;
  }

  score(card) {
    const source = this.identity(this.context.title, this.context.type);
    const candidate = this.identity(card.dataset.title, card.dataset.type);
    const words = new Set([...this.words(card.dataset.title), ...this.words(JSON.parse(card.dataset.tags || '[]').join(' '))]);
    const sourceWords = new Set([...this.words(this.context.title), ...this.words((this.context.tags || []).join(' '))]);
    const shared = [...source.words].filter((word) => candidate.words.has(word)).length;
    const material = ['cuero', 'lana', 'lino', 'algodon', 'acero', 'madera', 'latex'];
    const colors = ['marron', 'negro', 'blanco', 'rojo', 'azul', 'verde', 'gris', 'beige'];
    const styles = ['medieval', 'vikingo', 'celta', 'romano', 'pirata', 'renacentista', 'larp'];
    const overlap = (terms) => terms.some((word) => sourceWords.has(word) && words.has(word));
    const option = this.preferredOption(card);
    const price = Number(option?.dataset.priceCents || 0);
    const sourcePrice = Number(this.currentVariant?.price || 0);
    const priceDistance = sourcePrice > 0 ? Math.abs(price - sourcePrice) / sourcePrice : 0;
    return (source.kind === candidate.kind ? 2000 : 0) + (this.sameSize(card, option) ? 500 : 0)
      + (overlap(material) ? 100 : 0) + (overlap(styles) ? 60 : 0) + (overlap(colors) ? 40 : 0)
      + Math.min(shared, 10) * 5 - Math.min(priceDistance, 10);
  }

  update(variant) {
    this.currentVariant = variant || null;
    const inventory = this.owner?.inventory?.[String(variant?.id)];
    this.outOfStock = Boolean(variant && inventory?.tracked === true && Number(inventory.quantity) <= 0);
    this.toggle.hidden = true;
    this.error.hidden = true;
    this.setOpen(false);
    if (!this.outOfStock) return;
    if (this.loaded) this.render();
    else this.load();
  }

  setOpen(open) {
    if (open && this.toggle.hidden) return;
    const wasOpen = this.panel.open;
    if (open) {
      this.panel.hidden = false;
      if (!this.panel.open) this.panel.showModal();
    } else {
      if (this.panel.open) this.panel.close();
      this.panel.hidden = true;
      if (wasOpen && !this.toggle.hidden) this.toggle.focus();
    }
    this.toggle.setAttribute('aria-expanded', String(open));
  }

  async fetchCardPage(url, signal) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal });
    if (!response.ok) throw new Error('Stock alternatives: ' + response.status);
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = document.querySelector('[data-stock-alternatives-response]');
    if (!root) throw new Error('Invalid stock alternatives response');
    return { cards: [...root.querySelectorAll('template[data-stock-card]')], nextUrl: root.dataset.nextUrl || '' };
  }

  async fetchCards(url, signal) {
    return (await this.fetchCardPage(url, signal)).cards;
  }

  async fetchCollectionCards(collection, signal) {
    const cards = [];
    const visited = new Set();
    let nextUrl = collection.url;
    for (let page = 0; nextUrl && page < 5; page += 1) {
      const url = new URL(nextUrl, window.location.origin);
      if (url.origin !== window.location.origin) break;
      url.searchParams.set('section_id', 'product-stock-alternatives-collection');
      if (page === 0) url.searchParams.delete('page');
      if (visited.has(url.toString())) break;
      visited.add(url.toString());
      let result;
      try {
        result = await this.fetchCardPage(url.toString(), signal);
      } catch (error) {
        if (error.name === 'AbortError' || page === 0) throw error;
        break;
      }
      cards.push(...result.cards);
      nextUrl = result.nextUrl;
    }
    return cards;
  }

  async load() {
    if (this.loading || !this.controller) return;
    this.loading = true;
    const controller = this.controller;
    const responses = await Promise.allSettled([
      this.fetchCards(this.dataset.recommendationsUrl, controller.signal),
      ...this.collections().map((collection) => this.fetchCollectionCards(collection, controller.signal)),
    ]);
    if (this.controller !== controller || controller.signal.aborted) return;
    const unique = new Map();
    responses.forEach((response) => {
      if (response.status === 'fulfilled') response.value.forEach((card) => {
        if (card.dataset.productId !== this.dataset.productId && !unique.has(card.dataset.productId)) unique.set(card.dataset.productId, card);
      });
    });
    this.cards = [...unique.values()];
    this.loaded = responses.some((response) => response.status === 'fulfilled');
    this.loading = false;
    this.render();
  }

  render() {
    if (!this.outOfStock) return;
    const alternatives = this.alternatives();
    this.results.replaceChildren();
    const category = this.requestedCategory();
    if (alternatives.length && category && !alternatives.some(({ card }) => this.matchesCategory(card, category))) {
      const notice = document.createElement('p');
      notice.className = 'tm-stock-alternatives__category-notice';
      notice.textContent = this.dataset.missingCategoryLabel.replace('__category__', () => category.title);
      this.results.append(notice);
    }
    let grid;
    let previousGroup;
    alternatives.forEach(({ card, general }) => {
        const group = general ? 'other' : 'specific';
        if (group !== previousGroup) {
          if (group === 'other' && previousGroup === 'specific') {
            const heading = document.createElement('h3');
            heading.className = 'tm-stock-alternatives__group-heading';
            heading.textContent = this.otherAlternativesLabel();
            this.results.append(heading);
          }
          grid = document.createElement('div');
          grid.className = 'tm-stock-alternatives__grid';
          this.results.append(grid);
          previousGroup = group;
        }
        const fragment = card.content.cloneNode(true);
        const article = fragment.querySelector('[data-stock-alternative-card]');
        const select = article.querySelector('select');
        select.value = this.preferredOption(card)?.value || select.options[0].value;
        article.dataset.productId = card.dataset.productId;
        article.dataset.optionNames = card.dataset.optionNames;
        const suffix = this.panel.id;
        article.querySelectorAll('[id]').forEach((element) => { element.id += `-${suffix}`; });
        article.querySelectorAll('[for]').forEach((element) => { element.htmlFor += `-${suffix}`; });
        this.updateCard(article);
        grid.append(fragment);
    });
    this.toggle.hidden = alternatives.length === 0;
    this.fitToggle();
    if (this.toggle.hidden) this.setOpen(false);
  }

  fitToggle() {
    this.toggle.style.removeProperty('--stock-toggle-width');
    if (this.toggle.hidden || !this.toggleLabel || !window.matchMedia('(max-width: 749px)').matches) return;
    const range = document.createRange();
    range.selectNodeContents(this.toggleLabel);
    const lines = [...range.getClientRects()].filter((line) => line.width > 0);
    if (lines.length < 2) return;
    const buttonWidth = this.toggle.getBoundingClientRect().width;
    const labelWidth = this.toggleLabel.getBoundingClientRect().width;
    const longestLine = Math.max(...lines.map((line) => line.width));
    const fittedWidth = Math.min(buttonWidth, Math.ceil(longestLine + buttonWidth - labelWidth) + 1);
    this.toggle.style.setProperty('--stock-toggle-width', `${fittedWidth}px`);
  }

  updateCard(article) {
    const select = article.querySelector('select');
    select.hidden = select.options.length < 2;
    const selected = select.selectedOptions[0];
    const sizeLabel = article.querySelector('[data-stock-alternative-size]');
    const sizeIndex = this.sizeIndex(JSON.parse(article.dataset.optionNames || '[]'));
    const size = JSON.parse(selected.dataset.options || '[]')[sizeIndex];
    if (sizeLabel) {
      sizeLabel.hidden = sizeIndex < 0 || !size;
      const hasSourceSize = this.sizeIndex(this.context.optionNames || []) >= 0;
      const label = !hasSourceSize || this.sameSize(article, selected) ? this.dataset.sizeLabel : this.dataset.otherSizeLabel;
      sizeLabel.textContent = (label || '').replace('__size__', size || '');
    }
    article.querySelector('[data-stock-alternative-price]').textContent = selected.dataset.price;
    article.querySelectorAll('[data-stock-alternative-link]').forEach((link) => { link.href = selected.dataset.url; });
    const image = article.querySelector('img');
    if (image && selected.dataset.image) {
      image.alt = selected.dataset.imageAlt || '';
      image.src = selected.dataset.image;
      image.removeAttribute('srcset');
    }
  }

  async refresh() {
    const wasOpen = !this.panel.hidden;
    this.loaded = false;
    if (this.outOfStock) {
      await this.load();
      if (wasOpen && !this.toggle.hidden) this.setOpen(true);
    }
  }

  async checkBeforeAdd(event) {
    const form = event.target.closest('form[data-ajax-cart-add]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    event.stopPropagation();
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button.disabled) return;
    const select = form.querySelector('select');
    const variantId = select.value;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    select.disabled = true;
    this.error.hidden = true;
    const cart = window.medievalCart;
    const add = async () => {
      const url = new URL(select.selectedOptions[0].dataset.url, window.location.origin);
      url.searchParams.set('section_id', 'product-stock-alternative-card');
      let cards;
      try {
        cards = await this.fetchCards(url.toString(), this.controller.signal);
      } catch (error) {
        if (error.name === 'AbortError') return;
        throw new Error(this.dataset.stockCheckErrorLabel);
      }
      const option = cards.flatMap((card) => [...card.content.querySelectorAll('option')]).find((item) => item.value === variantId);
      if (!option) {
        this.error.textContent = this.dataset.stockChangedLabel;
        this.error.hidden = false;
        await this.refresh();
        return;
      }
      if (cart) {
        const cartUrl = new URL(`${cart.routes.cart}.js`, window.location.origin);
        const response = await fetch(cartUrl, { credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal });
        if (!response.ok) throw new Error(this.dataset.stockCheckErrorLabel);
        const currentCart = await response.json();
        const quantityInCart = currentCart.items.filter((item) => String(item.variant_id) === variantId)
          .reduce((quantity, item) => quantity + item.quantity, 0);
        if (quantityInCart >= Number(option.dataset.quantity)) {
          this.error.textContent = this.dataset.stockLimitLabel;
          this.error.hidden = false;
          return;
        }
      }
      select.disabled = false;
      if (cart) {
        const adding = cart.addProduct(form);
        select.disabled = true;
        await adding;
      } else {
        form.submit();
      }
    };
    try {
      if (cart) await cart.enqueue(add);
      else await add();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.error.textContent = this.dataset.stockCheckErrorLabel;
        this.error.hidden = false;
      }
    } finally {
      select.disabled = false;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }
}

if (!customElements.get('stock-alternatives')) customElements.define('stock-alternatives', MedievalStockAlternatives);
